import { FUEL_TYPE, TITLE_STATUS } from "../fees/contants";
// Vehicle Type is labelled from the ATC code the quote was built on, not from
// the fees dropdown's display table — see utility/vehicleTypeLabel.js.
import { vehicleTypeLabel } from "./vehicleTypeLabel";
import { buildFeeItems, splitFeeColumns } from "./feeItems";
import { isRefinance } from "./transactionType";
import { getEffectiveRegistrationMonths } from "./registrationMonthsField";

const currency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));

// Optional taxable-fee / trade-in inputs. These are all forwarded to ATC and
// they move the tax total, so the report has to print them whenever they were
// submitted — otherwise two quotes with different add-ons look identical on
// paper while returning different tax amounts. The `> 0` guard matches the
// output page (OutputLeftSide), so the PDF shows exactly what's on screen.
const moneyRow = (value, label) =>
  Number(value) > 0
    ? `
      <div class="flex flex-row justify-between gap-4">
        <div>${label}</div>
        <div class="font-medium text-right break-words min-w-0">${currency(value)}</div>
      </div>
    `
    : "";

const numberRow = (value, label) =>
  Number(value) > 0
    ? `
      <div class="flex flex-row justify-between gap-4">
        <div>${label}</div>
        <div class="font-medium text-right break-words min-w-0">${value}</div>
      </div>
    `
    : "";

const flagRow = (value, label) =>
  value
    ? `
      <div class="flex flex-row justify-between gap-4">
        <div>${label}</div>
        <div class="font-medium text-right break-words min-w-0">Yes</div>
      </div>
    `
    : "";

export const generateFeesReport = (feesFormData, response) => {
  // Flattening, alphabetical ordering and the two-column split all live in
  // utility/feeItems so this PDF, the output screen (OutputRightSide) and the
  // bot's summary PDF cannot drift apart. See the header comment there for why
  // the ordering is alphabetical rather than ATC's <FeeDetails> order.
  const [leftItems, rightItems] = splitFeeColumns(buildFeeItems(response));

  // Trade-in and rebate rows are purchase-only; see utility/transactionType.js.
  // Derived from feesFormData rather than taken as a parameter so neither caller
  // (pages/feeOutput, pages/multiPartFormOutput) needs a signature change.
  const isRefi = isRefinance(feesFormData);

  // Registration term actually quoted. `registrationmonths` in
  // redux-state/fees/api.js sends the user's entry or, when the field was
  // cleared, the per-state default — so the PDF has to print the same
  // resolution rather than the raw form value, or a quote built on Oregon's
  // mandatory 24-month term prints no term at all. See
  // utility/registrationMonthsField.js.
  const registrationMonths = getEffectiveRegistrationMonths(feesFormData);

  const renderFeeRows = (items) =>
    items
      .map(
        ([label, amount]) => `
          <div class="flex flex-row justify-between gap-4">
            <div>${label}</div>
            <div class="font-medium text-right">${amount}</div>
          </div>
        `
      )
      .join("");

  const dynamicFields = `
    <div class="flex flex-col w-full text-gray-700 gap-2">
      ${renderFeeRows(leftItems)}
    </div>
    <div class="flex flex-col w-full text-gray-700 gap-2">
      ${renderFeeRows(rightItems)}
    </div>
  `;

  return (
    `<div class="default-bg-color p-6">
      <div class="flex flex-col gap-4 w-full">
        <div class="flex flex-row text-gray-700 font-bold mb-2 items-center h-11">
          Your Information
        </div>

        <div class="bg-white p-6 rounded-xl text-sm border border-gray-300">
          <div class="text-gray-700 mb-3 font-bold">Location Information</div>
          <div class="flex flex-col lg:flex-row w-full gap-6 lg:gap-24 mb-6">
            <div class="flex flex-col w-full text-gray-700 gap-2">
              ${feesFormData?.zipCode ? `
                <div class="flex flex-row justify-between">
                  <div>Zip Code:</div>
                  <div class="font-medium">${feesFormData.zipCode}</div>
                </div>
              ` : ""}

              ${feesFormData?.city ? `
                <div class="flex flex-row justify-between">
                  <div>City:</div>
                  <div class="font-medium">${feesFormData.city}</div>
                </div>
              ` : ""}

              <div class="flex flex-row justify-between">
                <div>Outside city limits:</div>
                <div class="font-medium">${feesFormData.outSideCityLimit ? "Yes" : "No"}</div>
              </div>
            </div>

            <div class="flex flex-col w-full text-gray-700 gap-2">
              ${feesFormData?.state ? `
                <div class="flex flex-row justify-between">
                  <div>State:</div>
                  <div class="font-medium">${feesFormData.state}</div>
                </div>
              ` : ""}

              ${feesFormData?.country ? `
                <div class="flex flex-row justify-between">
                  <div>County:</div>
                  <div class="font-medium">${feesFormData.country}</div>
                </div>
              ` : ""}
            </div>
          </div>

          <div class="border-t-2 border-t-gray-100 mb-3"></div>
          <div class="text-gray-700 mt-6 font-bold mb-3">
            Vehicle Information
            ${feesFormData?.vinCode ? `
              <span class="text-xs text-gray-600 font-normal ml-1">${feesFormData.vinCode}</span>
            ` : ""}
          </div>

          <div class="flex flex-col lg:flex-row w-full gap-6 lg:gap-24 mb-6">
            <div class="flex flex-col w-full text-gray-700 gap-2">
              ${feesFormData?.modelYear ? `
                <div class="flex flex-row justify-between">
                  <div>Model Year:</div>
                  <div class="font-medium">${feesFormData.modelYear}</div>
                </div>
              ` : ""}

              ${feesFormData?.make ? `
                <div class="flex flex-row justify-between">
                  <div>Make:</div>
                  <div class="font-medium">${feesFormData.make}</div>
                </div>
              ` : ""}

              ${feesFormData?.model ? `
                <div class="flex flex-row justify-between">
                  <div>Model:</div>
                  <div class="font-medium">${feesFormData.model}</div>
                </div>
              ` : ""}

              ${feesFormData?.fuelType ? `
                <div class="flex flex-row justify-between">
                  <div>Fuel Type:</div>
                  <div class="font-medium">${FUEL_TYPE[feesFormData.fuelType]}</div>
                </div>
              ` : ""}

              ${feesFormData?.msrp ? `
                <div class="flex flex-row justify-between">
                  <div>MSRP:</div>
                  <div class="font-medium">
                    ${new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: 'USD' 
                    }).format(feesFormData.msrp)}
                  </div>
                </div>
              ` : ""}
            </div>

            <div class="flex flex-col w-full text-gray-700 gap-2">
              ${feesFormData?.vehicleType ? `
                <div class="flex flex-row justify-between">
                  <div>Vehicle Type:</div>
                  <div class="font-medium">${vehicleTypeLabel(feesFormData.vehicleType)}</div>
                </div>
              ` : ""}

              ${feesFormData?.vehicleWeight ? `
                <div class="flex flex-row justify-between">
                  <div>Vehicle Weight:</div>
                  <div class="font-medium">${Number(feesFormData?.vehicleWeight).toLocaleString()} lbs</div>
                </div>
              ` : ""}

              ${feesFormData?.gvw ? `
                <div class="flex flex-row justify-between">
                  <div>GVW:</div>
                  <div class="font-medium">${Number(feesFormData?.gvw).toLocaleString()} lbs</div>
                </div>
              ` : ""}

              ${feesFormData?.gvwr ? `
                <div class="flex flex-row justify-between">
                  <div>GVWR:</div>
                  <div class="font-medium">${Number(feesFormData?.gvwr).toLocaleString()} lbs</div>
                </div>
              ` : ""}

              ${feesFormData?.cityMpg !== '' && feesFormData?.cityMpg != null ? `
                <div class="flex flex-row justify-between">
                  <div>MPG (City):</div>
                  <div class="font-medium">${feesFormData.cityMpg}</div>
                </div>
              ` : ""}

              ${feesFormData?.highwayMpg !== '' && feesFormData?.highwayMpg != null ? `
                <div class="flex flex-row justify-between">
                  <div>Highway MPG:</div>
                  <div class="font-medium">${feesFormData.highwayMpg}</div>
                </div>
              ` : ""}

              ${feesFormData?.combinedMpg !== '' && feesFormData?.combinedMpg != null ? `
                <div class="flex flex-row justify-between">
                  <div>Combined MPG:</div>
                  <div class="font-medium">${feesFormData.combinedMpg}</div>
                </div>
              ` : ""}

              ${registrationMonths ? `
                <div class="flex flex-row justify-between">
                  <div>Registration Months:</div>
                  <div class="font-medium">${registrationMonths}</div>
                </div>
              ` : ""}
            </div>
          </div>

          <div class="border-t-2 border-t-gray-100 mb-3"></div>
          <div class="text-gray-700 mt-6 font-bold mb-3">Transaction Information</div>

          <div class="flex flex-col lg:flex-row w-full gap-6 lg:gap-24 mb-6">
            <div class="flex flex-col w-full text-gray-700 gap-2">
              ${feesFormData?.transactionType ? `
                <div class="flex flex-row justify-between">
                  <div>Transaction Type:</div>
                  <div class="font-medium">${feesFormData?.transactionType}</div>
                </div>
              ` : ""}

              <div class="flex flex-row justify-between">
                <div>Plate Transfer:</div>
                <div class="font-medium">${feesFormData?.plateTransfer ? "Yes" : "No"}</div>
              </div>

              ${feesFormData?.basePrice ? `
                <div class="flex flex-row justify-between">
                  <div>Base Price:</div>
                  <div class="font-medium">${new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(feesFormData.basePrice)}
                  </div>
                </div>
              ` : ""}

              ${moneyRow(feesFormData?.docsFees, 'Doc Fees:')}
              ${moneyRow(feesFormData?.Additions, 'Additional Fees:')}
              ${moneyRow(feesFormData?.TheftProtectionField, 'Theft Protection:')}
              ${moneyRow(feesFormData?.DeliveryFees, 'Delivery Fees:')}
              ${isRefi ? "" : numberRow(feesFormData?.TradeInYear, 'Trade In Year:')}
              ${isRefi ? "" : moneyRow(feesFormData?.ManufacturerRebate, 'Manufacturer Rebate:')}
            </div>

            <div class="flex flex-col w-full text-gray-700 gap-2">
              ${feesFormData?.plateType ? `
                <div class="flex flex-row justify-between">
                  <div>Plate Type:</div>
                  <div class="font-medium">${feesFormData?.plateType}</div>
                </div>
              ` : ""}

              ${feesFormData?.titleStatus ? `
                <div class="flex flex-row justify-between">
                  <div>Title Status:</div>
                  <div class="font-medium">${TITLE_STATUS[feesFormData?.titleStatus]}</div>
                </div>
              ` : ""}

              ${flagRow(feesFormData?.military, 'Military:')}
              ${moneyRow(feesFormData?.extendedWarranties, 'Extended Warranties:')}
              ${moneyRow(feesFormData?.ProtectionPackageField, 'Protection Packages:')}
              ${moneyRow(feesFormData?.InsurancePackages, 'Insurance Packages:')}
              ${moneyRow(feesFormData?.DealerServiceFees, 'Dealer Service Fees:')}
              ${isRefi ? "" : moneyRow(feesFormData?.TradeInValue, 'Trade In Value:')}
              ${moneyRow(feesFormData?.DealerInventoryTax, 'Dealer Inventory Tax:')}
            </div>
          </div>

          ${response["TransactionID"] ? `
            <div class="flex flex-col w-full text-gray-700 gap-2">
              <div class="flex flex-col sm:flex-row justify-between break-words gap-1 mt-6" style="page-break-inside: avoid; break-inside: avoid;">
                <div>Transaction ID:</div>
                <div class="font-medium max-w-full break-all text-right sm:text-left">
                  ${response["TransactionID"]}
                </div>
              </div>
            </div>
          ` : ""}
        </div>
      </div>

      <div class="mt-3">
        <div class='flex flex-col w-full'>
          <div class='flex flex-col gap-4 w-full'>
            <div class='flex flex-row justify-between mb-4 items-center'>
              <div class='text-gray-700 font-bold'>
                Fees Output
              </div>
            </div>
          </div>

          <div class='bg-white p-6 rounded-xl text-sm mb-4 mt-4 border border-gray-300'>
            <div class='text-gray-700 mb-3 font-bold'>
              Fee Output
            </div>

            <div class='flex flex-col lg:flex-row w-full gap-6 lg:gap-24 mb-2'>
              ${dynamicFields}
            </div>
          </div>

          ${response["Notes"] &&
            `<div class="bg-white p-6 rounded-xl text-sm border border-gray-300">
              <div class="mb-2 text-gray-700 font-bold">Notes</div>
              <div class="text-gray-600 text-xs">
                ${response["Notes"].split('|').map((note) => {
                    // Clean the incoming HTML (handle links so they open in a new tab when clicked)
                    const cleanNote = note ? note.replaceAll('<a ', '<a target="_blank" ') : '';

                    // Skip empty notes so we don't render a floating bullet
                    const textOnly = cleanNote.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
                    if (!textOnly.length) return "";

                    // Manually render the bullet in a flex row so html2canvas renders them properly
                    return `
                      <div style="display: flex; align-items: flex-start; margin-bottom: 4px;">
                        <span style="margin-right: 8px; flex-shrink: 0; font-weight: bold;">•</span>
                        <div class="sanitizedHtml" style="flex: 1;">${cleanNote.trim()}</div>
                      </div>
                    `;
                  }).join("")}
              </div>
            </div>`
          }
        </div>
      </div>
    </div>`
  )
}